import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft,
  Check,
  Circle,
  CheckCircle2,
  MinusCircle,
  Ban,
  Send,
  Save,
  Paperclip,
  Search,
} from "lucide-react";
import { COMPLIANCE_LEVEL_META } from "../contexts/ComplianceContext";
import { cn } from "../lib/utils";

const LEVELS = [
  { key: "fully_implemented", label: "Fully", icon: CheckCircle2, color: "text-green-600 border-green-500 bg-green-50 dark:bg-green-950/40" },
  { key: "partially_implemented", label: "Partially", icon: MinusCircle, color: "text-amber-600 border-amber-500 bg-amber-50 dark:bg-amber-950/40" },
  { key: "not_implemented", label: "Not", icon: Ban, color: "text-red-600 border-red-500 bg-red-50 dark:bg-red-950/40" },
  { key: "not_applicable", label: "N/A", icon: Circle, color: "text-slate-500 border-slate-400 bg-slate-50 dark:bg-slate-800" },
];

export default function AssessmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [drafts, setDrafts] = useState({}); // control_id -> { notes, evidence_ids }
  const [showEvPicker, setShowEvPicker] = useState(null);
  const [evList, setEvList] = useState([]);
  const [evQ, setEvQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/compliance/assessments/${id}`);
      setA(data);
      const d = {};
      data.controls.forEach((c) => {
        d[c.id] = {
          notes: c.response?.notes || "",
          evidence_ids: c.response?.evidence_ids || [],
        };
      });
      setDrafts(d);
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/compliance/evidence");
      setEvList(data.items || []);
    })();
  }, []);

  const pct = a?.compliance_percentage || 0;
  const isSubmitted = a?.status === "submitted";

  const answered = useMemo(
    () => (a?.controls || []).filter((c) => c.response).length,
    [a]
  );

  const setLevel = async (control, level) => {
    if (isSubmitted) return;
    setSaving((s) => ({ ...s, [control.id]: true }));
    try {
      const draft = drafts[control.id] || { notes: "", evidence_ids: [] };
      await api.post(`/compliance/assessments/${id}/responses`, {
        control_id: control.id,
        compliance_level: level,
        notes: draft.notes || null,
        evidence_ids: draft.evidence_ids || [],
      });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSaving((s) => ({ ...s, [control.id]: false }));
    }
  };

  const saveNotes = async (control) => {
    if (isSubmitted) return;
    if (!control.response) {
      toast.info("Select a compliance level first");
      return;
    }
    setSaving((s) => ({ ...s, [control.id]: true }));
    try {
      const draft = drafts[control.id];
      await api.post(`/compliance/assessments/${id}/responses`, {
        control_id: control.id,
        compliance_level: control.response.compliance_level,
        notes: draft.notes || null,
        evidence_ids: draft.evidence_ids || [],
      });
      await load();
      toast.success("Saved");
    } finally {
      setSaving((s) => ({ ...s, [control.id]: false }));
    }
  };

  const submit = async () => {
    if (!window.confirm("Submit this assessment? You will no longer be able to edit responses.")) return;
    try {
      await api.post(`/compliance/assessments/${id}/submit`);
      toast.success("Submitted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const toggleEvidence = (control_id, ev_id) => {
    setDrafts((prev) => {
      const cur = prev[control_id] || { notes: "", evidence_ids: [] };
      const set = new Set(cur.evidence_ids);
      if (set.has(ev_id)) set.delete(ev_id);
      else set.add(ev_id);
      return { ...prev, [control_id]: { ...cur, evidence_ids: Array.from(set) } };
    });
  };

  if (loading || !a) return <div className="h-40 bg-muted animate-pulse rounded" />;

  const filteredEv = evList.filter((e) =>
    !evQ ? true : (e.title + " " + (e.filename || "")).toLowerCase().includes(evQ.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <Link
        to="/assessments"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="w-4 h-4" /> All assessments
      </Link>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="primary">{a.framework_code}</Badge>
                <Badge
                  variant={a.status === "submitted" ? "success" : a.status === "in_progress" ? "info" : "neutral"}
                >
                  {a.status.replace("_", " ")}
                </Badge>
              </div>
              <h1 className="text-2xl font-semibold tracking-tight">{a.name}</h1>
              {a.scope && <p className="text-sm text-muted-foreground mt-1">{a.scope}</p>}
              <div className="text-xs text-muted-foreground mt-2">
                Framework: <span className="text-foreground font-medium">{a.framework_name}</span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-4xl font-semibold text-primary tabular-nums">{pct}%</div>
              <div className="text-xs text-muted-foreground">
                {answered} / {a.controls.length} answered
              </div>
              {!isSubmitted && (
                <Button data-testid="assessment-submit" onClick={submit} size="sm">
                  <Send className="w-4 h-4" /> Submit
                </Button>
              )}
            </div>
          </div>
          <div className="mt-4 h-2 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {a.controls.map((c, idx) => {
          const draft = drafts[c.id] || { notes: "", evidence_ids: [] };
          const respLevel = c.response?.compliance_level;
          return (
            <Card key={c.id} data-testid={`assessment-control-${c.id}`}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">
                      Question {idx + 1} of {a.controls.length}
                    </div>
                    <CardTitle>{c.question || c.title}</CardTitle>
                    {c.description && (
                      <CardDescription className="mt-2">{c.description}</CardDescription>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Level buttons */}
                <div className="flex flex-wrap gap-2">
                  {LEVELS.map((lv) => {
                    const active = respLevel === lv.key;
                    const Icon = lv.icon;
                    return (
                      <button
                        key={lv.key}
                        disabled={isSubmitted || saving[c.id]}
                        onClick={() => setLevel(c, lv.key)}
                        data-testid={`answer-${c.id}-${lv.key}`}
                        className={cn(
                          "inline-flex items-center gap-2 h-10 px-4 rounded-md border text-sm font-medium transition-colors",
                          active
                            ? lv.color + " border-current"
                            : "border-border text-muted-foreground hover:bg-muted",
                          (isSubmitted || saving[c.id]) && "opacity-60 cursor-not-allowed"
                        )}
                      >
                        <Icon className="w-4 h-4" />
                        {lv.label}
                      </button>
                    );
                  })}
                </div>

                {/* Notes */}
                <div>
                  <Textarea
                    disabled={isSubmitted}
                    placeholder="Notes and rationale…"
                    value={draft.notes}
                    onChange={(e) =>
                      setDrafts((prev) => ({
                        ...prev,
                        [c.id]: { ...(prev[c.id] || { evidence_ids: [] }), notes: e.target.value },
                      }))
                    }
                    data-testid={`answer-notes-${c.id}`}
                  />
                </div>

                {/* Evidence attachments */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="label-eyebrow">Attached evidence</div>
                    {!isSubmitted && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setShowEvPicker(c.id); setEvQ(""); }}
                        data-testid={`attach-evidence-${c.id}`}
                      >
                        <Paperclip className="w-4 h-4" /> Attach
                      </Button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {draft.evidence_ids.length === 0 && (
                      <span className="text-xs text-muted-foreground">No evidence attached.</span>
                    )}
                    {draft.evidence_ids.map((eid) => {
                      const ev = evList.find((x) => x.id === eid);
                      return (
                        <Badge key={eid} variant="primary" className="gap-1">
                          {ev?.title || "Evidence"}
                          {!isSubmitted && (
                            <button
                              onClick={() => toggleEvidence(c.id, eid)}
                              className="ml-1 hover:text-red-600"
                              aria-label="Remove"
                            >
                              ×
                            </button>
                          )}
                        </Badge>
                      );
                    })}
                  </div>
                </div>

                <div className="flex justify-end">
                  {!isSubmitted && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => saveNotes(c)}
                      data-testid={`save-notes-${c.id}`}
                      disabled={saving[c.id]}
                    >
                      <Save className="w-4 h-4" /> Save
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Evidence picker modal */}
      {showEvPicker && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowEvPicker(null)}>
          <div className="w-full max-w-lg max-h-[80vh] card-surface animate-fade-in flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-border">
              <h3 className="text-base font-semibold mb-2">Attach evidence</h3>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  className="h-10 w-full pl-9 pr-3 rounded-md border border-border bg-background text-sm"
                  placeholder="Search evidence…"
                  value={evQ}
                  onChange={(e) => setEvQ(e.target.value)}
                />
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {filteredEv.length === 0 && (
                <div className="p-6 text-center text-muted-foreground text-sm">
                  No evidence found.{" "}
                  <Link to="/evidence" className="text-primary">Upload some</Link>.
                </div>
              )}
              <ul className="divide-y divide-border">
                {filteredEv.map((ev) => {
                  const selected = drafts[showEvPicker]?.evidence_ids.includes(ev.id);
                  return (
                    <li
                      key={ev.id}
                      className="px-4 py-3 hover:bg-muted cursor-pointer flex items-center justify-between gap-3"
                      onClick={() => toggleEvidence(showEvPicker, ev.id)}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{ev.title}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {ev.filename} · v{ev.version} · {ev.status}
                        </div>
                      </div>
                      <div className={cn("w-5 h-5 rounded border flex items-center justify-center shrink-0",
                        selected ? "bg-primary border-primary text-primary-foreground" : "border-border")}>
                        {selected && <Check className="w-3 h-3" />}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="p-4 border-t border-border flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEvPicker(null)}>Close</Button>
              <Button
                onClick={() => {
                  const control = a.controls.find((c) => c.id === showEvPicker);
                  if (control?.response) {
                    saveNotes(control);
                  } else {
                    toast.info("Select a compliance level first, then attachments will save automatically.");
                  }
                  setShowEvPicker(null);
                }}
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
